import { baseApi } from "@/shared/api";
import { fileResponseHandler, createOrgSignedPatch, type FileResponse } from "@/shared/utils";
import type { SigningActionResponse } from "@/shared/types";
import type {
  ContractDetailResponse,
  ListContractsResponse,
  CombinedContractListItem,
  ContractsListResponse,
  CreateContractDto,
  CreateContractResponse,
  CreateContractAlreadyExistsResponse,
  ContractClausesResponse,
  ContractPreviewDataResponse,
  ContractFillDraftResponse,
  ContractFillDraftPayload,
  PatchContractRequest,
  PatchContractResponse,
} from "@/features/hr/contracts/types";

export const contractsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCombinedContractsList: builder.query<ContractsListResponse, void>({
      query: () => ({
        url: `/api/hr/contracts/list/`,
        method: "GET",
        credentials: "include",
      }),
      transformResponse(response: ContractsListResponse | CombinedContractListItem[]) {
        if (Array.isArray(response)) {
          return { contracts: response, fill_drafts: [] };
        }
        return response;
      },
      providesTags: [
        { type: "Contracts" as const, id: "LIST" },
        { type: "ResignationLetters" as const, id: "LIST" },
        { type: "Amendments" as const, id: "LIST" },
        { type: "ContractFillDrafts" as const, id: "LIST" },
      ],
    }),
    createContract: builder.mutation<CreateContractResponse | CreateContractAlreadyExistsResponse, CreateContractDto>({
      query: ({ application_id, ...body }) => ({
        url: `/api/hr/applications/${application_id}/create-contract/`,
        method: "POST",
        body,
        credentials: "include",
      }),
      invalidatesTags: [{ type: "Contracts" as const, id: "LIST" }, "Applications"],
    }),
    getNextContractNumber: builder.query<{ next: number }, void>({
      query: () => ({
        url: `/api/hr/contracts/next-number/`,
        method: "GET",
        credentials: "include" as const,
      }),
    }),
    getContractDetail: builder.query<ContractDetailResponse, number>({
      query: (id) => ({
        url: `/api/hr/contracts/${id}/`,
        method: "GET",
        credentials: "include",
      }),
      providesTags: (_result, _error, id) => [{ type: "Contracts" as const, id }],
    }),
    // DEPRECATED: use getCombinedContractsList instead
    getContracts: builder.query<ListContractsResponse[], void>({
      query: () => ({
        url: `/api/hr/contracts/`,
        method: "GET",
        credentials: "include",
      }),
      providesTags: [{ type: "Contracts" as const, id: "LIST" }],
    }),
    submitContractForSigning: builder.mutation<SigningActionResponse, { id: number; cms_signature: string; file_base64: string }>({
      query: ({ id, cms_signature, file_base64 }) => ({
        url: `/api/hr/contracts/${id}/submit-for-signing/`,
        method: "POST",
        body: { cms_signature, file_base64 },
        credentials: "include" as const,
      }),
      invalidatesTags: () => [
        { type: "Contracts" as const, id: "LIST" },
      ],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          contractsApi.util.updateQueryData("getContractDetail", id, (draft) => {
            draft.signing_status = 1;
            if (draft.signing_document) {
              Object.assign(draft.signing_document, createOrgSignedPatch());
            }
          }),
        );
      },
    }),
    getContractPreviewData: builder.query<ContractPreviewDataResponse, number>({
      query: (applicationId) => ({
        url: `/api/hr/applications/${applicationId}/contract-preview-data/`,
        method: "GET",
        credentials: "include",
      }),
      providesTags: (_result, _error, applicationId) => [{ type: "Applications" as const, id: applicationId }],
    }),
    downloadWorkContractPreview: builder.mutation<FileResponse, number>({
      query: (id) => ({
        url: `/api/hr/contracts/${id}/preview/`,
        method: "GET",
        credentials: "include",
        responseHandler: fileResponseHandler,
      }),
    }),
    getContractClauses: builder.query<ContractClausesResponse, void>({
      query: () => ({
        url: `/api/hr/contract-clauses/`,
        method: "GET",
        credentials: "include",
      }),
      providesTags: [{ type: "Contracts" as const, id: "CLAUSES" }],
    }),
    getContractFillDraft: builder.query<ContractFillDraftResponse, number>({
      query: (applicationId) => ({
        url: `/api/hr/applications/${applicationId}/fill-draft/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, applicationId) => [{ type: "ContractFillDrafts" as const, id: applicationId }],
    }),
    upsertContractFillDraft: builder.mutation<ContractFillDraftResponse, { applicationId: number } & ContractFillDraftPayload>({
      query: ({ applicationId, ...body }) => ({
        url: `/api/hr/applications/${applicationId}/fill-draft/`,
        method: "PUT",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, { applicationId }) => [
        { type: "ContractFillDrafts" as const, id: applicationId },
        { type: "ContractFillDrafts" as const, id: "LIST" },
      ],
    }),
    deleteContractFillDraft: builder.mutation<void, number>({
      query: (applicationId) => ({
        url: `/api/hr/applications/${applicationId}/fill-draft/`,
        method: "DELETE",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, applicationId) => [
        { type: "ContractFillDrafts" as const, id: applicationId },
        { type: "ContractFillDrafts" as const, id: "LIST" },
        { type: "Contracts" as const, id: "LIST" },
      ],
    }),
    deleteContract: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/hr/contracts/${id}/`,
        method: "DELETE",
        credentials: "include" as const,
      }),
      invalidatesTags: [{ type: "Contracts" as const, id: "LIST" }],
    }),
    patchContract: builder.mutation<PatchContractResponse, { id: number; data: PatchContractRequest }>({
      query: ({ id, data }) => ({
        url: `/api/hr/contracts/${id}/`,
        method: "PATCH",
        body: data,
        credentials: "include" as const,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Contracts" as const, id },
        { type: "Contracts" as const, id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetCombinedContractsListQuery,
  useCreateContractMutation,
  useGetNextContractNumberQuery,
  useGetContractDetailQuery,
  useLazyGetContractDetailQuery,
  useGetContractsQuery,
  useSubmitContractForSigningMutation,
  useGetContractPreviewDataQuery,
  useLazyGetContractPreviewDataQuery,
  useDownloadWorkContractPreviewMutation,
  useGetContractClausesQuery,
  useGetContractFillDraftQuery,
  useUpsertContractFillDraftMutation,
  useDeleteContractFillDraftMutation,
  useDeleteContractMutation,
  usePatchContractMutation,
} = contractsApi;
